import { clsx } from 'clsx';

import { Image } from '~/components/image';
import type { ImageProps as NextImageProps } from 'next/image';

export interface ImageAdaptiveProps {
  className?: string;
  imageSrc?: string;
  imageAlt: string;
  width?: number;
  height?: number;
  aspectRatio?: 'auto' | '1:1' | '4:3' | '16:9' | '3:4' | '5:6';
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  sizes?: string;
  quality?: number;
  priority?: boolean;
}

export function ImageAdaptive({
  className,
  imageSrc,
  imageAlt,
  width,
  height,
  aspectRatio = 'auto',
  objectFit = 'cover',
  sizes,
  quality = 75,
  priority = false,
}: ImageAdaptiveProps) {
  // Aspect ratio classes
  const aspectRatioClasses = {
    auto: '',
    '1:1': 'aspect-square',
    '4:3': 'aspect-[4/3]',
    '16:9': 'aspect-video',
    '3:4': 'aspect-[3/4]',
    '5:6': 'aspect-[5/6]',
  };

  // If no image, show placeholder
  if (!imageSrc) {
    const placeholderWidth = width || 1200;
    const placeholderHeight = height || 800;
    const aspectRatioClass = aspectRatio !== 'auto' ? aspectRatioClasses[aspectRatio] : '';

    return (
      <div
        className={clsx(
          'flex items-center justify-center border-2 border-dashed border-gray-300 bg-gray-50',
          aspectRatioClass,
          className,
        )}
        style={
          aspectRatio === 'auto'
            ? {
              width: placeholderWidth,
              height: placeholderHeight,
            }
            : undefined
        }
      >
        <div className="flex flex-col items-center gap-2 text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-12 w-12"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
            />
          </svg>
          <p className="text-sm font-medium">No image selected</p>
        </div>
      </div>
    );
  }

  // Default responsive sizes if not provided
  const defaultSizes =
    sizes ||
    '(min-width: 1536px) 1280px, (min-width: 1024px) 80vw, (min-width: 768px) 90vw, 100vw';

  // Object fit classes
  const objectFitClasses = {
    cover: 'object-cover',
    contain: 'object-contain',
    fill: 'object-fill',
    none: 'object-none',
    'scale-down': 'object-scale-down',
  };

  // If aspect ratio is set and dimensions aren't provided, use fill mode
  const useFill = aspectRatio !== 'auto' && (!width || !height);

  const imageProps: NextImageProps = {
    alt: imageAlt,
    src: imageSrc,
    quality,
    priority,
    loading: priority ? 'eager' : 'lazy',
    sizes: defaultSizes,
    className: clsx(objectFitClasses[objectFit], aspectRatioClasses[aspectRatio], className),
  };

  if (useFill) {
    return (
      <div className={clsx('relative', aspectRatioClasses[aspectRatio])}>
        <Image {...imageProps} fill />
      </div>
    );
  }

  return (
    <Image
      {...imageProps}
      width={width || 1200}
      height={height || 800}
    />
  );
}

